// ============================================================================
// @gs/i18n/config — initialisation i18next monorepo (BLITZ-I18N-FOUNDATION)
// ============================================================================
// Architecture hybride : namespace `common` partage (ce package) + namespace
// par app (apps/<app>/src/i18n/locales/<app>.{fr,en}.json). Design V2
// I18N_GUARDRAILS_GS_APPS (2026-05-11), pilotes V3 : ug (SPA) + api-portail (SSR).
// Decision business David 2026-05-11 : FR+EN obligatoire, toutes les apps
// (succursales ouest canadien BC/AB). Fallback TOUJOURS FR (marche primaire).

import i18next from 'i18next';
import type { InitOptions, Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './localeContext';
import type { SupportedLocale } from './localeContext';

import commonFr from './locales/common.fr.json';
import commonEn from './locales/common.en.json';

/** Cle localStorage de l'override manuel (selecteur Langue GsUserMenu). */
export const LOCALE_STORAGE_KEY = 'gs-locale';

export interface InitI18nOptions {
  appNamespace: string;
  resources: Record<SupportedLocale, Record<string, Record<string, unknown>>>;
  initialLocale?: SupportedLocale;
  /**
   * Hook futur — si defini, prime sur localStorage. Permet branchement a
   * ug.Users.preferred_locale (multi-company ADR-032) sans refactor.
   */
  getUserPreferredLocale?: () => SupportedLocale | null | undefined;
}

export function initI18n(options: InitI18nOptions): typeof i18next {
  const { appNamespace, resources, initialLocale, getUserPreferredLocale } = options;

  const mergedResources: Resource = {
    fr: { common: commonFr, ...resources.fr },
    en: { common: commonEn, ...resources.en },
  };

  const initOptions: InitOptions = {
    resources: mergedResources,
    lng: initialLocale ?? detectInitialLocale(getUserPreferredLocale),
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: appNamespace,
    ns: ['common', appNamespace],
    interpolation: {
      // React escape deja cote SPA. En SSR (api-portail) : escapeValue: true
      // via une init i18next core dediee (PAS ce helper react).
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  };

  void i18next.use(initReactI18next).init(initOptions);
  syncHtmlLang(i18next.language as SupportedLocale);
  return i18next;
}

function detectInitialLocale(
  getUserPreferredLocale?: () => SupportedLocale | null | undefined,
): SupportedLocale {
  // 1. Preference user (futur — ug.Users.preferred_locale via hook injecte)
  const userPref = getUserPreferredLocale?.();
  if (userPref && SUPPORTED_LOCALES.includes(userPref)) return userPref;

  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  // 2. localStorage (override manuel via le selecteur Langue)
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored as SupportedLocale)) {
    return stored as SupportedLocale;
  }

  // 3. navigator.language
  const navLang = window.navigator.language.split('-')[0].toLowerCase();
  if (SUPPORTED_LOCALES.includes(navLang as SupportedLocale)) {
    return navLang as SupportedLocale;
  }

  return DEFAULT_LOCALE;
}

function syncHtmlLang(locale: SupportedLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

/** Change la langue + persiste (localStorage) + synchronise <html lang>. */
export async function setLocale(locale: SupportedLocale): Promise<void> {
  await i18next.changeLanguage(locale);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
  syncHtmlLang(locale);
}
